import React, { useEffect, useState } from 'react';
import { useAngularService } from 'helpers/get_angular_service';
import sources from 'helpers/sources';
import api from 'helpers/api';
import notification from 'helpers/notification';
import currencyFormatter from 'helpers/currency_formatter';
import Button from '../../components/button';
import Error from '../../components/error_message';
import dateFormatter from 'helpers/date_formatter';
import classNames from 'classnames';
import Input from '../../components/input';
import Select from '../../components/select';
import humanize from 'helpers/humanize';
import Icon from '../../components/icon';

type RateType = 'hourly' | 'per_class';

interface PayRate {
  id?: number;
  amount?: string | number;
  created_at?: string;
  rate_type: RateType;
  errors?: any;
}

const ManagePayroll = ({ profile }) => {
  const [payRates, setPayRates] = useState<PayRate[]>([]);
  const [newPayRate, setNewPayRate] = useState<undefined | PayRate>();
  const [editPayRate, setEditPayRate] = useState<undefined | PayRate>();
  const [isLoading, setLoading] = useState(false);

  const $rootScope = useAngularService('$rootScope');
  const GTMService = useAngularService('GTMService');

  const gym = $rootScope.gym;

  const loadPayRates = () => {
    const url = sources.payRates(gym.id, profile.id);
    setLoading(true);
    api.get(url).then(
      (response) => {
        setPayRates(response);
        setLoading(false);
      },
      () => {
        notification.error('There was an error processing the request. Please try again');
        setLoading(false);
      },
    );
  };

  useEffect(() => {
    loadPayRates();
  }, []);

  const rateTypes: RateType[] = ['hourly', 'per_class'];

  const rateTypeMap = {
    hourly: 'hr',
    per_class: 'class',
  };

  const remove = (payRate) => {
    if (window.confirm('Are you sure you want to delete this pay rate?')) {
      GTMService.trackPage('Users - Payrolls', 'Delete');
      const url = `${sources.payRates(gym.id, profile.id)}/${payRate.id}`;
      api.delete(url).then(
        () => {
          loadPayRates();
        },
        () => {
          notification.error('Request could not be completed. Please try again');
        },
      );
    }
  };

  const save = () => {
    setLoading(true);
    const url = `${sources.payRates(gym.id, profile.id)}/${editPayRate.id}`;
    api.put(url, { pay_rate: editPayRate }).then(
      () => {
        loadPayRates();
        setEditPayRate(undefined);
      },
      (response) => {
        setLoading(false);
        setEditPayRate((item) => ({ ...item, errors: response.data.errors }));
      },
    );
  };

  const create = () => {
    GTMService.trackPage('Users - Payrolls', 'Add pay rate');
    setLoading(true);
    const url = `${sources.payRates(gym.id, profile.id)}`;
    api.post(url, { pay_rate: newPayRate }).then(
      () => {
        setNewPayRate(undefined);
        loadPayRates();
      },
      (response) => {
        setLoading(false);
        setNewPayRate((item) => ({ ...item, errors: response.data.errors }));
      },
    );
  };

  return (
    <div className="content-box">
      {!newPayRate && (
        <Button
          variant="primary"
          size="sm"
          className="pull-right"
          onClick={() => setNewPayRate({ rate_type: rateTypes[0] })}
        >
          Add pay rate
        </Button>
      )}

      <h3>Manage Trainer Pay Rates</h3>

      <table className="table worx-table worx-table--v2 worx-table--no-border-top">
        <tbody>
          {!!newPayRate && (
            <tr className="form-row">
              <td>
                <div className="input-group group-with-addon col-md-8">
                  <span className="input-group-addon">{gym.currency_symbol}</span>
                  <Input
                    min={0}
                    disabled={isLoading}
                    value={newPayRate.amount}
                    placeholder="Amount"
                    type="number"
                    onChange={(amount) => {
                      setNewPayRate((item) => ({ ...item, amount }));
                    }}
                  />
                </div>
                <Error error={newPayRate.errors?.amount} />
              </td>
              <td>
                <div className="row">
                  <div className="col-md-8">
                    <Select
                      disabled={isLoading}
                      value={newPayRate.rate_type}
                      options={rateTypes.map((item) => ({ value: item, label: humanize(item) }))}
                      onChange={(rate_type) => {
                        setNewPayRate((item) => ({ ...item, rate_type: rate_type as RateType }));
                      }}
                    />
                  </div>
                </div>
              </td>
              <td className="worx-context-menu text-right">
                <Button variant="success" size="sm" onClick={create} disabled={isLoading}>
                  Save
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setNewPayRate(undefined)}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
              </td>
            </tr>
          )}
          {payRates.map((payRate) => (
            <tr
              key={payRate.id}
              className={classNames({ 'form-row': editPayRate?.id === payRate.id })}
            >
              {editPayRate?.id === payRate.id ? (
                <>
                  <td>
                    <div className="form-group">
                      <div className="input-group group-with-addon col-md-8">
                        <span className="input-group-addon">{gym.currency_symbol}</span>

                        <Input
                          min={0}
                          disabled={isLoading}
                          value={editPayRate.amount}
                          placeholder="Amount"
                          type="number"
                          onChange={(amount) => {
                            setEditPayRate((item) => ({ ...item, amount }));
                          }}
                        />
                      </div>
                      <Error error={editPayRate.errors?.amount} />
                    </div>
                  </td>
                  <td>
                    <div className="row">
                      <div className="col-md-8">
                        <Select
                          disabled={isLoading}
                          value={editPayRate.rate_type}
                          options={rateTypes.map((item) => ({
                            value: item,
                            label: humanize(item),
                          }))}
                          onChange={(rate_type) => {
                            setEditPayRate((item) => ({
                              ...item,
                              rate_type: rate_type as RateType,
                            }));
                          }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="worx-context-menu text-right">
                    <Button variant="success" onClick={() => save()} size="sm">
                      Save
                    </Button>
                    <Button onClick={() => setEditPayRate(undefined)} size="sm">
                      Cancel
                    </Button>
                  </td>
                </>
              ) : (
                <>
                  <td>
                    <div className="table-row-description">
                      {currencyFormatter(payRate.amount, gym.currency_symbol)}/
                      {rateTypeMap[payRate.rate_type]}
                    </div>
                  </td>

                  <td>
                    <div className="table-row-timestamp">
                      added {dateFormatter(payRate.created_at, 'fromNow')}
                    </div>
                  </td>
                  <td className="worx-context-menu text-right">
                    <Button
                      onClick={() => {
                        GTMService.trackPage('Users - Payrolls', 'Edit');
                        setEditPayRate(payRate);
                      }}
                      variant="icon--v2"
                    >
                      <Icon name="edit" width={20} height={20} />
                    </Button>
                    <Button onClick={() => remove(payRate)} variant="icon--v2">
                      <Icon name="trash" width={20} height={20} />
                    </Button>
                  </td>
                </>
              )}
            </tr>
          ))}

          {!payRates.length && (
            <tr>
              <td colSpan={3}>No pay rates found</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

angular.module('gym').value('ManagePayroll', ManagePayroll);
